import ReactModal from "react-modal";
import { X, AlertTriangle, Navigation, Globe } from "lucide-react";
import dayjs from "dayjs";
import { toast } from "sonner";

import { TranslatedText } from "@/components/common/text";
import { useLoadFinderStore } from "@/state/loadFinderStore";
import { TruckStatus, trailerTypeSymbols, truckBadgeStyles, truckStatusText, filterBadgeStyles } from "@/utils/constants";

const TrailerTypeBadge = ({ type }) => {
    const symbol = trailerTypeSymbols[type] || "V";
    return (
        <span className="flex items-center justify-center w-4 h-5 text-[12px] font-semibold rounded text-cm-p-text-primary bg-cm-p-surface-container">
            {symbol}
        </span>
    );
};

const LoadStatusBadge = ({ status }) => {
    const isFree = status === TruckStatus.FREE.id;
    const style = truckBadgeStyles[status] || truckBadgeStyles.FREE;
    const text = truckStatusText[status] || truckStatusText.FREE;

    return (
        <span
            className="flex items-center gap-1 text-[12px] font-medium uppercase tracking-tight pl-1.5 pr-2 py-1 rounded-md h-5"
            style={style}
        >
            {isFree && <AlertTriangle className="w-3 h-3" />}
            {text}
        </span>
    );
};

const FilterStatusBadge = ({ hasFilter }) => {
    const style = hasFilter ? filterBadgeStyles.HAS_FILTER : filterBadgeStyles.NO_FILTER;
    const text = hasFilter ? "Has filters" : "No filter";

    return (
        <span
            className="flex items-center gap-1 text-[12px] font-medium uppercase tracking-tight pl-1.5 pr-2 py-1 rounded-md h-5"
            style={style}
        >
            <Globe className="w-3 h-3" />
            {text}
        </span>
    );
};

export const DeleteTruckConfirmModal = ({ isOpen, onClose, truck, hasFilter = false, onDeleted }) => {
    // Delete via the company endpoint (keyed by truckId, authorized by the caller's company).
    // This works for every truck the user can see, including their own.
    const deleteCompanyTruck = useLoadFinderStore((s) => s.deleteCompanyTruck);
    const fetchTrucks = useLoadFinderStore((s) => s.fetchTrucks);
    const fetchCompanyTrucks = useLoadFinderStore((s) => s.fetchCompanyTrucks);
    const fetchTruckFilters = useLoadFinderStore((s) => s.fetchTruckFilters);
    const loading = useLoadFinderStore((s) => s.deleteTruckState?.loading);

    if (!truck) return null;

    const truckName = truck.truckName;
    const trailerType = truck.trailerType;
    const status = truck.status || TruckStatus.FREE.id;
    const location = truck.location ? `${truck.location.city}, ${truck.location.state}` : null;
    const deliveryDate = truck.deliveryDate ? dayjs(truck.deliveryDate).format("M/D") : null;
    const isLoaded = status === TruckStatus.LOADED.id;
    const isOffDuty = status === TruckStatus.OFF_DUTY.id;
    const dateText = deliveryDate
        ? isLoaded
            ? ` on ${deliveryDate}`
            : isOffDuty
              ? `, ready ${deliveryDate}`
              : `, available from ${deliveryDate}`
        : "";

    const handleConfirm = async () => {
        try {
            await deleteCompanyTruck(truck.truckId);
            toast.success(`Truck ${truckName} has been deleted`, { className: "cm-toast-success", toasterId: "top-right" });
            onClose();
            onDeleted?.(truck.truckId);
            // Refresh trucks lists
            fetchTrucks();
            fetchCompanyTrucks();
            fetchTruckFilters();
        } catch {
            toast.error("Failed to delete truck", { className: "cm-toast-error", toasterId: "top-right" });
        }
    };

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            overlayClassName="ReactModal__Overlay fixed inset-0 bg-black/70 z-modal flex items-center justify-center"
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
        >
            <div className="bg-cm-p-surface rounded-xl w-[480px] flex flex-col pointer-events-auto shadow-xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between pl-5 pr-3 py-3 border-b border-cm-p-outline">
                    <h2 className="text-[18px] font-medium text-cm-p-text-primary tracking-tight">
                        <TranslatedText>Delete this truck?</TranslatedText>
                    </h2>
                    <button type="button" onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded hover:bg-cm-p-surface-hover">
                        <X className="w-5 h-5 text-cm-p-text-secondary" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-4 pt-5 pb-8 flex flex-col gap-3">
                    <p className="text-[14px] text-cm-p-text-secondary px-1">
                        <TranslatedText>Are you sure you want to delete this truck?</TranslatedText>
                    </p>

                    {/* Truck card */}
                    <div className="border-[1.5px] border-cm-p-outline rounded-lg px-3 py-2.5 flex flex-col gap-2">
                        {/* Upper part */}
                        <div className="flex flex-col gap-0">
                            {/* Truck name */}
                            <span className="text-[16px] font-medium text-cm-p-text-primary">{truckName}</span>

                            {/* Pills row */}
                            <div className="flex items-center gap-1 py-0.5">
                                <TrailerTypeBadge type={trailerType} />
                                <LoadStatusBadge status={status} />
                                <FilterStatusBadge hasFilter={hasFilter} />
                            </div>
                        </div>

                        {/* Location info */}
                        {location && (
                            <div className="flex items-center gap-2">
                                <Navigation className="w-4 h-4 text-cm-p-text-secondary shrink-0" />
                                <span className="text-[14px] text-cm-p-text-secondary">
                                    {isLoaded ? "Delivering in " : "Waiting in "}
                                    {location}
                                    {dateText}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end items-center gap-[10px] px-4 py-4 border-t border-cm-p-outline">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-[120px] h-[44px] rounded-md bg-cm-p-surface-container text-cm-p-text-primary text-[14px] font-medium focus:outline-none"
                    >
                        <TranslatedText>Cancel</TranslatedText>
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={loading}
                        className="w-[130px] h-[44px] rounded-md bg-[#e25353] text-white text-[14px] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d04545]"
                    >
                        <TranslatedText>Yes, delete</TranslatedText>
                    </button>
                </div>
            </div>
        </ReactModal>
    );
};
